import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminAnalysisDetail } from "../../lib/admin";
import { ArrowLeft, Loader2 } from "lucide-react";
import { PaidReport, FreeReport } from "../../components/Reports";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (rel) => (rel?.startsWith("http") ? rel : rel ? `${BACKEND_URL}${rel}` : null);

function Section({ title, children }) {
    return (
        <div className="rounded-[20px] bg-white p-5 shadow-soft ring-1 ring-charcoal/5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{title}</div>
            <div className="mt-3 space-y-2 text-[13px] text-charcoal">{children}</div>
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div className="grid grid-cols-3 gap-3 border-b border-charcoal/5 py-1.5 last:border-0">
            <span className="text-[11px] uppercase tracking-[0.16em] text-charcoal/55">{k}</span>
            <span className="col-span-2 break-words text-[12.5px]">{v ?? "—"}</span>
        </div>
    );
}

export default function AdminAnalysisDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminAnalysisDetail(id).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return <div className="flex h-60 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-gold" /></div>;
    }
    if (!data) {
        return <div className="rounded-[20px] bg-white p-6 shadow-soft">Not found.</div>;
    }

    const ss = data.story_studio || null;
    const gallery = data.gallery || {};

    return (
        <div className="space-y-6" data-testid="admin-analysis-detail">
            <Link to="/admin" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-charcoal/60 hover:text-charcoal">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
            </Link>

            <div className="rounded-[24px] bg-charcoal p-6 text-ivory shadow-soft md:p-8">
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Analysis · {data.id.slice(0, 8)}</div>
                <h1 className="mt-2 font-serif text-3xl">{data.name || "Unnamed couple"}</h1>
                <p className="mt-1 text-[13px] text-ivory/70">{data.occasion || "—"}</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <Section title="Metadata">
                    <KV k="Mode" v={data.mode} />
                    <KV k="Created" v={data.created_at ? new Date(data.created_at).toLocaleString() : "—"} />
                    <KV k="Paid" v={data.paid ? `Yes · ₹${Math.round((data.amount_paise || 0) / 100).toLocaleString("en-IN")} · via ${data.paid_via || "—"}` : "No"} />
                    <KV k="Paid at" v={data.paid_at ? new Date(data.paid_at).toLocaleString() : "—"} />
                    <KV k="Report status" v={data.report_status} />
                    <KV k="Gallery status" v={data.gallery_status} />
                    <KV k="Unlock status" v={data.unlock_status} />
                </Section>

                <Section title="Questionnaire">
                    {data.questionnaire ? Object.entries(data.questionnaire).map(([k, v]) => (
                        <KV key={k} k={k} v={typeof v === "object" ? JSON.stringify(v) : v} />
                    )) : <p className="text-[12.5px] text-charcoal/55">No questionnaire data.</p>}
                </Section>
            </div>

            {ss && (
                <Section title="Story Studio answers">
                    {Object.entries(ss).map(([k, v]) => (
                        <KV key={k} k={k} v={Array.isArray(v) ? v.join(", ") : v} />
                    ))}
                </Section>
            )}

            {Object.keys(gallery).length > 0 && (
                <div className="rounded-[20px] bg-white p-5 shadow-soft ring-1 ring-charcoal/5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Generated gallery</div>
                    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                        {Object.entries(gallery).map(([slot, url]) =>
                            url ? (
                                <figure key={slot} className="overflow-hidden rounded-[14px] ring-1 ring-charcoal/5">
                                    <img src={absUrl(url)} alt={slot} className="aspect-[3/4] w-full object-cover" />
                                    <figcaption className="px-2 py-1.5 text-[10px] uppercase tracking-[0.18em] text-charcoal/55">{slot}</figcaption>
                                </figure>
                            ) : null,
                        )}
                    </div>
                </div>
            )}

            {data.free && (
                <div className="rounded-[20px] bg-white p-2 shadow-soft ring-1 ring-charcoal/5 md:p-4">
                    <FreeReport
                        report={data.free_report || { ...data.free, mode: "couple" }}
                        onUnlock={() => {}}
                        priceLabel={`₹${Math.round((data.amount_paise || 124900) / 100).toLocaleString("en-IN")}`}
                    />
                </div>
            )}
            {data.paid_report && (
                <div className="rounded-[20px] bg-white p-2 shadow-soft ring-1 ring-charcoal/5 md:p-4">
                    <PaidReport report={data.paid_report} gallery={gallery} />
                </div>
            )}
        </div>
    );
}
